<template>
    <div class="cost-manager">
        <div class="cost-manager__head">
            <div class="cost-manager__nav">
                <b-button size="sm" variant="outline-secondary" @click="moveMonth(-1)">◀</b-button>
                <span class="cost-manager__month">{{ monthLabel }}</span>
                <b-button size="sm" variant="outline-secondary" @click="moveMonth(1)">▶</b-button>
            </div>

            <span v-if="isSaved" class="badge badge-info ml-3">저장됨</span>
            <span v-else-if="copiedFrom" class="badge badge-warning ml-3">
                {{ copiedFromLabel }}에서 복사됨 · 저장 전
            </span>
            <span v-else class="badge badge-light ml-3">신규</span>

            <div class="flex-grow-1"></div>

            <!-- 두 단추는 한 덩이 — 좁으면 같이 아래 줄 오른쪽으로 -->
            <div class="d-flex ml-auto">
                <b-button size="sm" variant="outline-secondary" class="mr-2 text-nowrap" :disabled="!dirty" @click="reset">
                    되돌리기
                </b-button>
                <b-button size="sm" variant="primary" class="text-nowrap" :disabled="!dirty || saving" @click="save">
                    <i class="fas fa-save mr-1"></i>저장
                </b-button>
            </div>
        </div>

        <div class="cost-manager__body">
            <table class="cost-manager__table">
                <thead>
                    <tr>
                        <th style="width: 230px;">항목</th>
                        <th style="width: 150px;">분류</th>
                        <th style="width: 150px;" class="num">금액</th>
                        <th style="width: 160px;">세금</th>
                        <th style="width: 130px;" class="num">실지급액</th>
                        <th>메모</th>
                        <th style="width: 44px;"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in draft" :key="i" :class="{ 'is-auto': item.autoKey }">
                        <td data-label="항목">
                            <template v-if="item.autoKey">
                                <span class="badge badge-info mr-1">자동</span>{{ item.name }}
                            </template>
                            <b-form-input v-else v-model="item.name" size="sm" placeholder="항목명" @input="dirty = true" />
                        </td>
                        <td data-label="분류">
                            <span v-if="item.autoKey">{{ item.category }}</span>
                            <b-form-input
                                v-else
                                v-model="item.category"
                                size="sm"
                                list="ledger-cost-categories"
                                placeholder="직접 입력"
                                @input="dirty = true"
                            />
                        </td>
                        <td data-label="금액" class="num">
                            <span v-if="item.autoKey">{{ Number(item.amount).toLocaleString() }}</span>
                            <b-form-input
                                v-else
                                v-model.number="item.amount"
                                size="sm"
                                type="number"
                                min="0"
                                class="text-right"
                                @input="dirty = true"
                            />
                        </td>
                        <td data-label="세금">
                            <span v-if="item.autoKey" class="tax-vat">{{ taxLabel(item.tax) }}</span>
                            <b-form-select v-else v-model="item.tax" size="sm" :options="taxOptions" @change="dirty = true" />
                        </td>
                        <td data-label="실지급액" class="num">
                            <span v-if="item.tax === 'wht'" class="tax-wht">{{ payoutOf(item).toLocaleString() }}</span>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td data-label="메모">
                            <span v-if="item.autoKey" class="text-muted">{{ item.memo }}</span>
                            <b-form-input v-else v-model="item.memo" size="sm" placeholder="-" @input="dirty = true" />
                        </td>
                        <td class="text-center">
                            <span v-if="item.autoKey" class="text-muted" title="정산에서 자동으로 가져온 항목이라 지울 수 없어요">–</span>
                            <b-button v-else size="sm" variant="link" class="text-danger p-0" @click="removeRow(i)">✕</b-button>
                        </td>
                    </tr>
                    <tr v-if="!draft.length">
                        <td colspan="7" class="text-center text-muted py-3">
                            등록된 비용이 없습니다. 아래에서 항목을 추가하세요.
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td colspan="2">합계</td>
                        <td class="num"><strong>{{ totalAmount.toLocaleString() }}원</strong></td>
                        <td colspan="4" class="summary">
                            <span class="tax-vat">부가세 환급분 {{ totalRefund.toLocaleString() }}원</span>
                            <span class="text-muted"> → 요약장부에서 되돌려줌</span>
                            <template v-if="totalWithholding > 0">
                                <span class="text-muted"> · </span>
                                <span class="tax-wht">원천징수 {{ totalWithholding.toLocaleString() }}원</span>
                                <span class="text-muted"> (손익엔 영향 없고 실제 나가는 돈만 줄어듦)</span>
                            </template>
                        </td>
                    </tr>
                </tfoot>
            </table>

            <b-button size="sm" variant="outline-secondary" class="mt-3" @click="addRow">
                <i class="fas fa-plus mr-1"></i>항목 추가
            </b-button>

            <datalist id="ledger-cost-categories">
                <option v-for="c in knownCategories" :key="c" :value="c"></option>
            </datalist>

            <div class="cost-manager__note">
                새 달을 처음 열면 지난달 항목이 그대로 복사돼 있습니다. 고쳐서 저장해도
                <strong>지난달 장부는 바뀌지 않습니다.</strong>
                촬영비 같은 1회성 비용은 집행한 달에 전액 넣으세요.<br>
                <span class="badge badge-info">자동</span> 표시된 항목은 세이버팩 · 비즈니스인사이트 · 쿠팡라이브처럼 정산에서 바로 빠지는 돈이라 수집할 때 자동으로 채워집니다.
            </div>

            <!-- ── 상품·날짜 비용 ─────────────────────────────── -->
            <h6 class="cost-manager__section">
                상품 · 날짜 비용
                <small class="text-muted ml-2">
                    특정 상품에 특정 날짜로 쓴 돈. 일별 · 상품별 표의 <strong>기타비용</strong> 칸에 반영됩니다
                </small>
            </h6>

            <table class="cost-manager__table">
                <thead>
                    <tr>
                        <th style="width: 140px;">날짜</th>
                        <th style="width: 300px;">상품</th>
                        <th style="width: 150px;">분류</th>
                        <th style="width: 140px;" class="num">금액</th>
                        <th style="width: 160px;">세금</th>
                        <th>메모</th>
                        <th style="width: 44px;"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="c in monthProductCosts" :key="c._id">
                        <td data-label="날짜">{{ c.date }}</td>
                        <td data-label="상품" class="ellipsis" :title="productNameOf(c.vendorItemId)">{{ productNameOf(c.vendorItemId) }}</td>
                        <td data-label="분류">{{ c.category || '-' }}</td>
                        <td data-label="금액" class="num">{{ Number(c.amount).toLocaleString() }}</td>
                        <td data-label="세금" :class="taxClass(c.tax)">{{ taxLabel(c.tax) }}</td>
                        <td data-label="메모">{{ c.memo || '-' }}</td>
                        <td class="text-center">
                            <b-button size="sm" variant="link" class="text-danger p-0" @click="$emit('remove-cost', c)">✕</b-button>
                        </td>
                    </tr>

                    <!-- 입력 행 -->
                    <tr class="cost-manager__input-row">
                        <td data-label="날짜"><b-form-input v-model="newCost.date" type="date" size="sm" /></td>
                        <td data-label="상품">
                            <b-form-select v-model="newCost.vendorItemId" :options="productSelectOptions" size="sm" />
                        </td>
                        <td data-label="분류">
                            <b-form-input v-model="newCost.category" size="sm" list="ledger-cost-categories" placeholder="직접 입력" />
                        </td>
                        <td data-label="금액" class="num">
                            <b-form-input v-model.number="newCost.amount" type="number" min="0" size="sm" class="text-right" placeholder="0" />
                        </td>
                        <td data-label="세금"><b-form-select v-model="newCost.tax" :options="taxOptions" size="sm" /></td>
                        <td data-label="메모"><b-form-input v-model="newCost.memo" size="sm" placeholder="-" /></td>
                        <td class="text-center">
                            <b-button size="sm" variant="primary" class="px-2" @click="addProductCost">＋</b-button>
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td colspan="3">{{ monthLabel }} 합계</td>
                        <td class="num"><strong>{{ productCostTotal.toLocaleString() }}원</strong></td>
                        <td colspan="3" class="hint">
                            상품별 수익성에 반영됩니다 (고정비와 달리 기여이익에서 차감)
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script>
/**
 * 기간 고정비 관리 — 월 스냅샷 방식
 *
 * 월마다 독립된 비용표를 갖는다. 저장 안 된 달을 열면 직전 달 항목을 복사해 보여주고,
 * 저장하는 순간 그 달의 값으로 굳는다(과거 월은 영향 없음).
 * 분류는 자유 입력이며 요약장부의 행이 여기서 만들어진다.
 */
export default {
    name: 'LedgerCostManager',
    props: {
        // [{ month:'YYYY-MM', items:[{name,category,amount,tax,memo}] }]
        fixedCosts: { type: Array, default: () => [] },
        // 상품 직접비 [{ _id, date, vendorItemId, category, amount, tax, memo }]
        productCosts: { type: Array, default: () => [] },
        // 고를 수 있는 상품 [{ value, text }]
        products: { type: Array, default: () => [] },
        saving: { type: Boolean, default: false },
        // 열 때 맞출 날짜 'YYYY-MM-DD' — 일별장부 «비용 입력» 이 조회 기간 끝날을 준다(판 #452). 빈 값 = 이번 달·오늘(요약장부)
        initialDate: { type: String, default: '' }
    },
    data() {
        return {
            currentMonth: '',
            draft: [],
            dirty: false,
            copiedFrom: '',
            newCost: { date: '', vendorItemId: '', category: '', amount: null, tax: 'none', memo: '' },
            taxOptions: [
                { value: 'vat', text: '부가세 포함 10%' },
                { value: 'wht', text: '원천징수 3.3%' },
                { value: 'none', text: '세금 없음' }
            ]
        };
    },
    computed: {
        monthLabel() {
            if (!this.currentMonth) return '';
            return this.currentMonth.slice(0, 4) + '년 ' + parseInt(this.currentMonth.slice(5, 7), 10) + '월';
        },
        copiedFromLabel() {
            if (!this.copiedFrom) return '';
            return parseInt(this.copiedFrom.slice(5, 7), 10) + '월';
        },
        savedDoc() {
            return this.fixedCosts.find(f => f.month === this.currentMonth) || null;
        },
        isSaved() {
            return !!this.savedDoc && !this.dirty;
        },
        totalAmount() {
            return this.draft.reduce((s, it) => s + (Number(it.amount) || 0), 0);
        },
        totalRefund() {
            return this.draft.reduce((s, it) => s + (it.tax === 'vat' ? Math.round((Number(it.amount) || 0) / 11) : 0), 0);
        },
        totalWithholding() {
            return this.draft.reduce((s, it) => s + (it.tax === 'wht' ? Math.round((Number(it.amount) || 0) * 0.033) : 0), 0);
        },
        // 전에 쓴 분류를 자동완성으로 제안 (관리 화면 없이 재사용되게)
        knownCategories() {
            const set = [];
            this.fixedCosts.forEach(f => (f.items || []).forEach(it => {
                const c = (it.category || '').trim();
                if (c && !set.includes(c)) set.push(c);
            }));
            this.productCosts.forEach(c => {
                const v = (c.category || '').trim();
                if (v && !set.includes(v)) set.push(v);
            });
            return set.sort();
        },

        // 이 달의 상품 직접비
        monthProductCosts() {
            return this.productCosts
                .filter(c => c.date && String(c.date).slice(0, 7) === this.currentMonth)
                .sort((a, b) => (a.date < b.date ? -1 : 1));
        },
        productCostTotal() {
            return this.monthProductCosts.reduce((s, c) => s + (Number(c.amount) || 0), 0);
        },
        productSelectOptions() {
            if (!this.products.length) return [{ value: '', text: '(상품DB를 불러오는 중)' }];
            return [{ value: '', text: '상품 선택' }].concat(this.products);
        }
    },
    watch: {
        fixedCosts() {
            if (!this.dirty) this.loadMonth();
        },
        // 모달은 화면이 뜰 때 한 번 만들어진다 — 다시 열 때 받은 날짜로 맞춘다
        initialDate() { this.applyInitialDate(); }
    },
    created() {
        const now = new Date();
        this.currentMonth = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
        this.loadMonth();
        this.newCost.date = now.getFullYear() + '-' +
            String(now.getMonth() + 1).padStart(2, '0') + '-' +
            String(now.getDate()).padStart(2, '0');
        this.applyInitialDate();
    },
    methods: {
        // 받은 날짜(initialDate)의 달을 보이고 상품비용 날짜 칸도 그 날로 — 빈 값이거나 저장 안 한 변경이 있으면 그대로
        applyInitialDate() {
            const d = this.initialDate;
            if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d) || this.dirty) return;
            this.currentMonth = d.slice(0, 7);
            this.loadMonth();
            this.newCost.date = d;
        },
        // 저장본이 있으면 그걸, 없으면 직전 달을 복사해서 보여준다
        loadMonth() {
            this.copiedFrom = '';
            const saved = this.savedDoc;
            if (saved) {
                this.draft = (saved.items || []).map(it => ({ ...it }));
                this.dirty = false;
                return;
            }
            const prev = this.fixedCosts
                .filter(f => f.month < this.currentMonth)
                .sort((a, b) => (a.month < b.month ? 1 : -1))[0];
            if (prev) {
                this.copiedFrom = prev.month;
                // 정산에서 자동으로 들어오는 항목은 복사하지 않는다 (그 달 값이 따로 들어온다)
                this.draft = (prev.items || []).filter(it => !it.autoKey).map(it => ({ ...it }));
            } else {
                this.draft = [];
            }
            this.dirty = false;
        },
        moveMonth(delta) {
            if (this.dirty && !confirm('저장하지 않은 변경이 있습니다. 이동할까요?')) return;
            const y = parseInt(this.currentMonth.slice(0, 4), 10);
            const m = parseInt(this.currentMonth.slice(5, 7), 10) + delta;
            const d = new Date(y, m - 1, 1);
            this.currentMonth = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
            this.loadMonth();
            this.syncNewCostDate();
        },
        // 상품비용 입력의 기본 날짜를 보고 있는 달에 맞춘다
        syncNewCostDate() {
            const first = this.currentMonth + '-01';
            if (!this.newCost.date || String(this.newCost.date).slice(0, 7) !== this.currentMonth) {
                this.newCost.date = first;
            }
        },
        addRow() {
            this.draft.push({ name: '', category: '', amount: 0, tax: 'vat', memo: '' });
            this.dirty = true;
        },
        removeRow(i) {
            this.draft.splice(i, 1);
            this.dirty = true;
        },
        payoutOf(item) {
            return Math.round((Number(item.amount) || 0) * 0.967);
        },
        taxLabel(tax) {
            if (tax === 'vat') return '부가세 포함';
            if (tax === 'wht') return '원천징수 3.3%';
            return '세금 없음';
        },
        taxClass(tax) {
            if (tax === 'vat') return 'tax-vat';
            if (tax === 'wht') return 'tax-wht';
            return 'text-muted';
        },
        productNameOf(vendorItemId) {
            const found = this.products.find(p => String(p.value) === String(vendorItemId));
            return found ? found.text : String(vendorItemId);
        },
        addProductCost() {
            if (!this.newCost.date) { alert('날짜를 선택해 주세요.'); return; }
            if (!this.newCost.vendorItemId) { alert('상품을 선택해 주세요.'); return; }
            if (!this.newCost.amount || Number(this.newCost.amount) <= 0) { alert('금액을 입력해 주세요.'); return; }
            this.$emit('add-cost', {
                date: this.newCost.date,
                vendorItemId: String(this.newCost.vendorItemId),
                category: (this.newCost.category || '').trim(),
                amount: Number(this.newCost.amount),
                tax: this.newCost.tax,
                memo: this.newCost.memo || ''
            });
            this.newCost.category = '';
            this.newCost.amount = null;
            this.newCost.memo = '';
        },
        reset() {
            this.loadMonth();
        },
        save() {
            this.$emit('save', {
                month: this.currentMonth,
                items: this.draft.map(it => {
                    const row = {
                        name: (it.name || '').trim(),
                        category: (it.category || '').trim(),
                        amount: Number(it.amount) || 0,
                        tax: it.tax || 'none',
                        memo: it.memo || ''
                    };
                    // 자동 등록분은 키를 지켜야 다음 수집 때 중복으로 쌓이지 않는다
                    if (it.autoKey) row.autoKey = it.autoKey;
                    return row;
                })
            });
            this.dirty = false;
            this.copiedFrom = '';
        }
    }
};
</script>

<style lang="scss" scoped>
.cost-manager {
    // 좁은 화면(390)에선 단추를 아래 줄로 — 한 줄에 몰면 «되돌리기»·«저장» 이 두 줄로 꺾였다(판 #452 화면 판)
    &__head {
        display: flex;
        flex-wrap: wrap;
        row-gap: 8px;
        align-items: center;
        padding: 0 0 10px;
        margin-bottom: 12px;
        border-bottom: 1px solid #eef1f4;
    }
    &__nav {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    &__month {
        font-weight: 700;
        font-size: 15px;
        min-width: 110px;
        text-align: center;
    }
    &__body {
        padding: 0;
        overflow-x: auto;
    }
    &__table {
        border-collapse: collapse;
        width: 100%;
        min-width: 980px;
        font-size: 13px;

        th, td {
            padding: 6px 8px;
            border-bottom: 1px solid #f0f3f6;
            text-align: left;
            vertical-align: middle;
        }
        thead th {
            background: #f7f9fb;
            border-bottom: 2px solid #e1e6ea;
            color: #5a6b7b;
            font-weight: 600;
        }
        td.num, th.num { text-align: right; }
        tfoot td {
            background: #f7f9fb;
            font-weight: 700;
            border-top: 2px solid #e1e6ea;
        }
        tfoot .summary { font-weight: 400; font-size: 12px; }
    }
    &__note {
        margin-top: 14px;
        background: #f8fbff;
        border: 1px solid #dbe8f5;
        border-left: 3px solid #2b6cb0;
        padding: 10px 12px;
        border-radius: 0 4px 4px 0;
        font-size: 12px;
        line-height: 1.8;
        color: #5a6b7b;
    }
    &__section {
        margin: 26px 0 12px;
        padding-top: 18px;
        border-top: 2px solid #e1e6ea;
        font-size: 14px;
        font-weight: 700;
        small { font-weight: 400; font-size: 12px; }
    }
    &__input-row {
        background: #f8fbff;
        td { border-top: 1px solid #dbe8f5; }
    }
    .ellipsis {
        max-width: 300px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .hint { font-weight: 400; font-size: 12px; color: #7f8c9a; }

    // 좁은 화면(≤576px): 표 행을 카드처럼 세로로 — min-width 980 표를 가로로 밀어야 35% 만 보였다(판 #452 화면 판). 칸 이름은 td data-label
    @media (max-width: 576px) {
        &__table {
            display: block;
            min-width: 0;

            thead { display: none; }
            tbody, tfoot { display: block; }
            tbody tr {
                display: block;
                padding: 6px 0;
                border-bottom: 1px solid #e1e6ea;
            }
            tbody td {
                display: flex;
                align-items: center;
                gap: 8px;
                border-bottom: 0;
                padding: 3px 4px;
                text-align: left;

                &[data-label]::before {
                    content: attr(data-label);
                    flex: 0 0 64px;
                    font-size: 12px;
                    color: #7f8c9a;
                }
                > .form-control, > .custom-select, > input, > select { flex: 1 1 auto; min-width: 0; }
            }
            td.num { text-align: left; }
            td.text-center { justify-content: flex-end; }
            .ellipsis { max-width: none; white-space: normal; }
            tfoot tr { display: flex; flex-wrap: wrap; }
            // 칸이 남은 폭을 나눠 채운다 — 글 폭만 차지하면 합계 줄 배경이 본문보다 짧았다(390 293/336px)
            tfoot td { display: block; flex: 1 1 auto; border-top: 0; }
        }
    }
    tr.is-auto td { background: #f7fbff; }
    .tax-vat { color: #1e874b; }
    .tax-wht { color: #8e44ad; }
}
</style>
